import type { ReactNode } from 'react';
import React from 'react';

import { Modal as AntModal, Button, Tooltip, Grid } from 'antd';

const { useBreakpoint } = Grid;

interface ModalProps {
  open: boolean;
  keepMounted?: boolean;
  size?: false | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  textTitle: string;
  textReturnButton: string;
  textProceedButton?: string | JSX.Element;
  hideReturnButton?: boolean;
  hideProceedButton?: boolean;
  isProceedButtonDisabled?: boolean;
  enableScroll?: boolean;
  children: ReactNode;
  setClose: () => void;
  handleProceedClick?: (any: any) => void;
  handleReturnClick?: (any: any) => void;
  textTooltipProceedButton?: string;
}

const sizeToWidth: Record<string, number> = {
  xs: 400,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536
};

function Modal({
  open,
  size = 'sm',
  textTitle,
  textReturnButton,
  textProceedButton,
  children,
  textTooltipProceedButton,
  isProceedButtonDisabled = false,
  hideReturnButton = false,
  hideProceedButton = false,
  enableScroll = false,
  setClose,
  handleProceedClick,
  handleReturnClick = setClose
}: ModalProps) {
  const screens = useBreakpoint();
  const isMobile = !screens.sm;

  const modalWidth = size ? sizeToWidth[size] || 600 : 600;

  return (
    <AntModal
      open={open}
      onCancel={setClose}
      width={isMobile ? '100vw' : modalWidth}
      className={
        isMobile ? '[&_.ant-modal]:top-0! [&_.ant-modal]:m-0! [&_.ant-modal]:max-w-full! [&_.ant-modal]:p-0!' : ''
      }
      styles={{
        body: {
          overflowY: enableScroll || isMobile ? 'auto' : 'visible',
          fontFamily: '"Roboto","Helvetica Neue","Arial",sans-serif'
        }
      }}
      title={textTitle}
      closable
      footer={
        <div className='flex justify-end gap-2 max-sm:flex-col'>
          {!hideReturnButton && (
            <Button
              id='modal-return-button'
              type='text'
              onClick={handleReturnClick}
              className='leading-[150%] max-sm:w-full'
            >
              {textReturnButton}
            </Button>
          )}
          {!hideProceedButton &&
            (textTooltipProceedButton ? (
              <Tooltip title={textTooltipProceedButton} placement='top'>
                <Button
                  type='primary'
                  id='modal-proceed-button'
                  onClick={handleProceedClick}
                  className='text-sm leading-[150%] font-semibold max-sm:w-full md:min-w-[100px]'
                  disabled={isProceedButtonDisabled}
                >
                  {textProceedButton}
                </Button>
              </Tooltip>
            ) : (
              <Button
                type='primary'
                id='modal-proceed-button'
                onClick={handleProceedClick}
                className='text-sm leading-[150%] font-semibold max-sm:w-full md:min-w-[100px]'
                disabled={isProceedButtonDisabled}
              >
                {textProceedButton}
              </Button>
            ))}
        </div>
      }
    >
      <div className='font-inherit m-0'>{children}</div>
    </AntModal>
  );
}

export default Modal;
